import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  applyRoutePreset,
  cloneRoutePreset,
  deleteRoutePreset,
  exportRoutePreset,
  getRoutePresetById,
  getRoutePresets,
  getRoutePresetsPagedList,
  type RoutePresetExportPayload,
  type RoutePresetListParams,
} from "src/services/routePresetsService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

type QueryOptions = { enabled?: boolean } & Record<string, unknown>;

export const useQueryGetRoutePresetsPagedList = (
  params: RoutePresetListParams = {},
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.routePresetList, params, language],
      queryFn: () => getRoutePresetsPagedList(params, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetRoutePresets = (options: QueryOptions = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.routePresetNames, language],
      queryFn: () => getRoutePresets(language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetRoutePresetById = (
  presetId?: string | number,
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!presetId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.routePresetDetail, presetId, language],
      queryFn: () => getRoutePresetById(presetId as string | number, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useMutationCloneRoutePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { id: string | number; name: string }) =>
      cloneRoutePreset(params, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetNames });
    },
  });
};

export const useMutationExportRoutePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: RoutePresetExportPayload) => exportRoutePreset(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetNames });
    },
  });
};

export const useMutationUseRoutePreset = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { id: string | number; vehicleId: string | number }) =>
      applyRoutePreset(params, language),
  });
};

export const useMutationDeleteRoutePreset = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (presetId: string | number) => deleteRoutePreset(presetId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetList });
      queryClient.invalidateQueries({ queryKey: QueryNames.routePresetNames });
    },
  });
};
